// One employee's performance profile — month by month, quarters, the year,
// and every incentive with how it was worked out. An employee may open only
// their own (the server answers 403 otherwise).
import { Fragment, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Bar as RBar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowLeft, BadgeIndianRupee, CalendarDays, ChevronDown, ChevronRight, Layers, Star, Target, TrendingUp, Users } from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import { currentFy, currentMonthKey, inr, pctText, STATUS_LABEL, STATUS_TONE, useEmployeePerformance, type EmployeeMonth } from '../api';
import { Breakdown } from '../components/IncentivesPanel';
import { Bar, C, Chip, Kpi, Panel, toneFor } from '../components/ui';

const lakh = (v: number) => (Math.abs(v) >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : `₹${Math.round(v / 1000)}k`);
const ROLE: Record<string, string> = { superadmin: 'Super Admin', admin: 'Admin', department_admin: 'Department Administrator', manager: 'Manager', team_lead: 'Team Lead', bde: 'BDE', field_executive: 'Field Executive', employee: 'Sales Executive' };
const PART_LABEL: Record<string, string> = { achievement: 'Sales achievement', conversion: 'Conversion', newCustomer: 'New customers', retention: 'Retention', activity: 'Follow-ups' };
const tip = { contentStyle: { borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 } };

export function EmployeePerformancePage() {
  const { id = '' } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const fy = sp.get('fy') || currentFy();
  const { data, isLoading, error } = useEmployeePerformance(id, fy);
  const [open, setOpen] = useState<string | null>(null);
  if (isLoading) return <div className="flex justify-center py-24"><Spinner /></div>;
  if (error || !data) {
    const msg = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Could not load this performance profile.';
    return <div className="p-6"><p className="rounded-xl bg-rose-50 p-4 text-sm text-rose-700">{msg}</p></div>;
  }
  const e = data.employee;
  const c = data.current;
  const s = data.incentiveSummary;
  const ys = data.year.score;
  const prev = data.monthly.length > 1 && c?.key !== currentMonthKey() ? data.monthly[data.monthly.length - 2] : null;
  const delta = (a: number, b?: number) => (b && b > 0 ? Math.round(((a - b) / b) * 1000) / 10 : null);

  return (
    <div className="min-h-full bg-slate-50/60">
      <div className="bg-gradient-to-br from-[#0f2a6b] via-[#1d4ed8] to-[#2563eb] px-4 pb-14 pt-5 text-white md:px-8">
        <div className="mx-auto max-w-[1400px]">
          <button type="button" onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold ring-1 ring-white/20 hover:bg-white/20"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
          <div className="flex flex-wrap items-center gap-5">
            <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 text-2xl font-bold ring-1 ring-white/25">{e.name.split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase()}</span>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight">{e.name}</h1>
              <p className="mt-1 text-sm text-blue-100">{[e.employeeCode || e.username, e.designation || ROLE[e.role] || e.role, e.department, e.territory, e.branch].filter(Boolean).join(' · ')}</p>
              <p className="mt-0.5 text-xs text-blue-200">{e.managerName ? `Reports to ${e.managerName}` : 'No manager set'}{e.joined ? ` · joined ${new Date(e.joined).toLocaleDateString('en-IN')}` : ''}</p>
            </div>
            <div className="ml-auto flex flex-wrap gap-3">
              <Head label="Rank this month" value={c?.rank ? `#${c.rank}` : '—'} />
              <Head label="Grade" value={c?.score === null || c?.score === undefined ? (c?.grade || '—') : c.grade} />
              <Head label={`FY ${data.fy} score`} value={ys.score === null ? 'No target' : `${ys.score} · ${ys.grade}`} />
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto -mt-9 flex max-w-[1400px] flex-col gap-5 px-4 pb-10 md:px-8">
        {c && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi label={`${c.label} sales`} value={inr(c.grossSales)} icon={TrendingUp} accent={C.blue} delta={delta(c.grossSales, prev?.grossSales)}
              spark={data.monthly.map((m) => m.grossSales)} sub={`New ${inr(c.newSales)} · Existing ${inr(c.existingSales)}`} />
            <Kpi label="Target achievement" value={pctText(c.achievementPct)} icon={Target} accent={toneFor(c.achievementPct).bar}
              progress={c.achievementPct} sub={c.target ? `of ${inr(c.target)} target` : 'No target set for this month'} />
            <Kpi label="Conversions" value={String(c.conversions)} icon={Users} accent={C.violet} spark={data.monthly.map((m) => m.conversions)}
              sub={`${c.newCustomers} new · win rate ${pctText(c.conversionRatePct)}`} />
            <Kpi label="Incentive this month" value={inr(c.incentive)} icon={BadgeIndianRupee} accent={C.green} spark={data.monthly.map((m) => m.incentive)}
              sub={STATUS_LABEL[c.incentiveStatus] || c.incentiveStatus} />
          </div>
        )}

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
          <Panel title="Performance trend" subtitle="New and existing sales against target, with incentive" icon={TrendingUp} className="xl:col-span-2">
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={data.monthly}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={lakh} tick={{ fontSize: 11, fill: '#64748b' }} width={56} axisLine={false} tickLine={false} />
                <Tooltip {...tip} formatter={(v: number) => inr(v)} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <RBar isAnimationActive={false} dataKey="newSales" name="New sales" stackId="s" fill={C.green} />
                <RBar isAnimationActive={false} dataKey="existingSales" name="Existing sales" stackId="s" fill={C.blue} radius={[5, 5, 0, 0]} />
                <Line isAnimationActive={false} dataKey="target" name="Target" stroke={C.amber} strokeWidth={2} strokeDasharray="6 4" dot={false} />
                <Line isAnimationActive={false} dataKey="incentive" name="Incentive" stroke={C.violet} strokeWidth={2} dot={{ r: 2 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Score breakdown" subtitle={`FY ${data.fy} — what the score is made of`} icon={Star}>
            {ys.parts ? (
              <div className="flex flex-col gap-3">
                <div className="flex items-baseline gap-2"><span className="text-4xl font-bold text-slate-900">{ys.score}</span><Chip className="bg-blue-50 text-blue-700">Grade {ys.grade}</Chip></div>
                {Object.entries(ys.parts).map(([k, v]) => (
                  <div key={k}>
                    <div className="mb-1 flex justify-between text-xs"><span className="text-slate-600">{PART_LABEL[k] || k}</span><b className="tabular-nums text-slate-900">{Math.round(v * 10) / 10}</b></div>
                    <div className="h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${Math.min(100, v)}%` }} /></div>
                  </div>
                ))}
              </div>
            ) : <p className="py-10 text-center text-sm text-slate-400">No score — no target was set for this year yet.</p>}
          </Panel>
        </div>

        <Panel title="Month by month" subtitle="Open a month to see exactly how its incentive was calculated" icon={CalendarDays} pad={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="bg-slate-50 text-left text-data-xs font-semibold uppercase tracking-wide text-slate-500">
                {['Month', 'Target', 'Sales', 'New / Existing', 'Achievement', 'Conversions', 'Rank', 'Score', 'Incentive', 'Status'].map((h) => <th key={h} className="whitespace-nowrap px-4 py-2.5">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.monthly.map((m) => <MonthRow key={m.key} m={m} open={open === m.key} onToggle={() => setOpen(open === m.key ? null : m.key)} />)}
              </tbody>
            </table>
          </div>
        </Panel>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <Panel title="Quarterly" icon={Layers}>
            <div className="grid grid-cols-2 gap-3">
              {data.quarterly.map((x) => {
                const t = toneFor(x.achievementPct);
                return (
                  <div key={x.quarter} className="rounded-xl border border-slate-200 p-3.5">
                    <div className="flex items-center justify-between"><b className="text-slate-900">{x.quarter}</b><Chip className={t.soft}>{pctText(x.achievementPct)}</Chip></div>
                    <p className="mt-1.5 text-lg font-bold tabular-nums text-slate-900">{inr(x.grossSales)}</p>
                    <p className="text-data-xs text-slate-500">Target {x.target ? inr(x.target) : '—'} · {x.conversions} conversions</p>
                    <p className="mt-1 text-data-xs text-slate-500">Incentive <b className="text-slate-800">{inr(x.incentive)}</b></p>
                  </div>
                );
              })}
            </div>
          </Panel>
          <Panel title={`Year — FY ${data.fy}`} icon={BadgeIndianRupee}>
            <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
              {[
                ['Annual target', data.year.target ? inr(data.year.target) : '—'], ['Annual sales', inr(data.year.grossSales)],
                ['Achievement', pctText(data.year.achievementPct)], ['New sales', inr(data.year.newSales)], ['Existing sales', inr(data.year.existingSales)],
                ['Customers new / existing', `${data.year.newCustomers} / ${data.year.existingCustomers}`], ['Conversions', String(data.year.conversions)],
                ['Average monthly sales', inr(data.year.avgMonthlySales)], ['Incentive earned', inr(s.earned)], ['Paid', inr(s.paid)], ['Pending', inr(s.pending)],
                ['Rejected', inr(s.rejected)], ['Average monthly incentive', inr(s.avgMonthly)], ['Highest incentive', inr(s.highest)], ['Best month', s.bestMonth || '—'],
              ].map(([k, v]) => <div key={k} className="flex justify-between border-b border-slate-100 py-2 text-[13px]"><span className="text-slate-500">{k}</span><b className="tabular-nums text-slate-900">{v}</b></div>)}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function Head({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-[7.5rem] rounded-xl bg-white/10 px-4 py-2.5 ring-1 ring-white/15">
      <p className="text-data-xs font-semibold uppercase tracking-[0.12em] text-blue-100">{label}</p>
      <p className="mt-0.5 text-lg font-bold">{value}</p>
    </div>
  );
}

function MonthRow({ m, open, onToggle }: { m: EmployeeMonth; open: boolean; onToggle: () => void }) {
  return (
    <Fragment>
      <tr onClick={onToggle} className={`cursor-pointer border-t border-slate-100 ${open ? 'bg-blue-50/40' : 'hover:bg-slate-50/70'}`}>
        <td className="whitespace-nowrap px-4 py-2.5 font-semibold text-slate-900"><span className="inline-flex items-center gap-1.5">{open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}{m.label}</span></td>
        <td className="px-4 py-2.5 tabular-nums text-slate-500">{m.target ? inr(m.target) : '—'}</td>
        <td className="px-4 py-2.5 font-semibold tabular-nums text-slate-900">{inr(m.grossSales)}</td>
        <td className="px-4 py-2.5 text-[12px] tabular-nums"><span className="text-emerald-700">{inr(m.newSales)}</span><span className="text-slate-300"> / </span><span className="text-blue-700">{inr(m.existingSales)}</span></td>
        <td className="px-4 py-2.5"><div className="flex items-center gap-2"><span className={`w-12 font-semibold tabular-nums ${toneFor(m.achievementPct).text}`}>{pctText(m.achievementPct)}</span><Bar pct={m.achievementPct} /></div></td>
        <td className="px-4 py-2.5 tabular-nums">{m.conversions} <span className="text-data-xs text-slate-400">({m.newCustomers} new)</span></td>
        <td className="px-4 py-2.5 text-slate-600">{m.rank ? `#${m.rank}` : '—'}</td>
        <td className="whitespace-nowrap px-4 py-2.5">{m.score === null || m.grade === 'No target' ? <span className="text-data-xs text-slate-400">No target</span> : <><b>{m.score}</b> <span className="text-data-xs text-slate-500">{m.grade}</span></>}</td>
        <td className="px-4 py-2.5 font-bold tabular-nums text-slate-900">{inr(m.incentive)}</td>
        <td className="px-4 py-2.5"><Chip className={STATUS_TONE[m.incentiveStatus] || 'bg-slate-100 text-slate-600'}>{STATUS_LABEL[m.incentiveStatus] || m.incentiveStatus}</Chip></td>
      </tr>
      {open && (
        <tr className="bg-blue-50/30"><td colSpan={10} className="px-4 pb-4 pt-1">
          <Breakdown inc={{ breakdown: m.breakdown, finalAmount: m.incentive, adjustments: m.adjustments || [], history: m.history || [], originalAmount: m.incentive }} />
        </td></tr>
      )}
    </Fragment>
  );
}

export default EmployeePerformancePage;
